import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ProductForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stockQuantity: '',
    category: '',
    brand: '',
    partNumber: '',
    imageUrls: ['']
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    loadDropdownOptions();
    if (isEdit && id) {
      loadProduct();
    }
  }, [isEdit, id]);

  const loadDropdownOptions = async () => {
    try {
      const [categoriesResponse, brandsResponse] = await Promise.all([
        productAPI.getCategories(),
        productAPI.getBrands()
      ]);
      
      setCategories(categoriesResponse.data || []);
      setBrands(brandsResponse.data || []);
    } catch (error) {
      console.error('Failed to load dropdown options:', error);
    }
  };

  const loadProduct = async () => {
    try {
      setIsLoadingProduct(true);
      const response = await productAPI.getProduct(id);
      const product = response.data;
      
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        stockQuantity: product.stockQuantity || '',
        category: product.category || '',
        brand: product.brand || '',
        partNumber: product.partNumber || '',
        imageUrls: product.imageUrls && product.imageUrls.length > 0 
          ? product.imageUrls 
          : ['']
      });
    } catch (error) {
      console.error('Failed to load product:', error);
      toast.error('Failed to load product details');
      navigate('/admin/products');
    } finally {
      setIsLoadingProduct(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Product name must be less than 255 characters';
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Price must be a positive number';
    }

    if (!formData.stockQuantity || parseInt(formData.stockQuantity) < 0) {
      newErrors.stockQuantity = 'Stock quantity must be a non-negative number';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }

    if (!formData.partNumber.trim()) {
      newErrors.partNumber = 'Part number is required';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (formData.brand && formData.brand.length > 100) {
      newErrors.brand = 'Brand name must be less than 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsLoading(true);
    
    try {
      // Clean up the form data
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        stockQuantity: parseInt(formData.stockQuantity),
        imageUrls: formData.imageUrls.filter(url => url.trim())
      };

      if (isEdit) {
        await productAPI.updateProduct(id, submitData);
        toast.success('Product updated successfully');
      } else {
        await productAPI.createProduct(submitData);
        toast.success('Product created successfully');
      }
      
      navigate('/admin/products');
    } catch (error) {
      console.error('Failed to save product:', error);
      
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid product data';
        toast.error(errorMessage);
        
        // Handle specific validation errors from backend
        if (errorMessage.includes('part number')) {
          setErrors(prev => ({ ...prev, partNumber: 'Part number already exists' }));
        }
      } else {
        toast.error(`Failed to ${isEdit ? 'update' : 'create'} product. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleImageUrlChange = (index, value) => {
    const newImageUrls = [...formData.imageUrls];
    newImageUrls[index] = value;
    setFormData(prev => ({ ...prev, imageUrls: newImageUrls }));
  };

  const addImageUrl = () => {
    setFormData(prev => ({
      ...prev,
      imageUrls: [...prev.imageUrls, '']
    }));
  };

  const removeImageUrl = (index) => {
    if (formData.imageUrls.length > 1) {
      const newImageUrls = formData.imageUrls.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, imageUrls: newImageUrls }));
    }
  };

  const containerStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px'
  };

  const headerStyle = {
    marginBottom: '30px'
  };

  const titleStyle = {
    margin: '0 0 8px 0',
    fontSize: '28px',
    color: '#333'
  };

  const formStyle = {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: '8px',
    border: '1px solid #dee2e6'
  };

  const formGroupStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#495057'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '16px',
    boxSizing: 'border-box'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '100px',
    resize: 'vertical'
  };

  const errorStyle = {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '4px'
  };

  const buttonGroupStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '30px'
  };

  const submitButtonStyle = {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold'
  };

  const cancelButtonStyle = {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer'
  };

  const rowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  };

  if (isLoadingProduct) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>Loading product details...</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>
          {isEdit ? 'Edit Product' : 'Add New Product'}
        </h1>
        <p style={{ color: '#666', margin: 0 }}>
          {isEdit 
            ? 'Update the product information below' 
            : 'Fill in the details to create a new product'
          }
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} style={formStyle}>
        {/* Product Name */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Product Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter product name"
            style={{
              ...inputStyle,
              borderColor: errors.name ? '#dc3545' : '#ced4da'
            }}
          />
          {errors.name && <div style={errorStyle}>{errors.name}</div>}
        </div>

        {/* Description */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Enter product description"
            style={{
              ...textareaStyle,
              borderColor: errors.description ? '#dc3545' : '#ced4da'
            }}
          />
          {errors.description && <div style={errorStyle}>{errors.description}</div>}
        </div>

        {/* Price and Stock */}
        <div style={rowStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Price ($) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              placeholder="0.00"
              style={{
                ...inputStyle,
                borderColor: errors.price ? '#dc3545' : '#ced4da'
              }}
            />
            {errors.price && <div style={errorStyle}>{errors.price}</div>}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Stock Quantity *</label>
            <input
              type="number"
              min="0"
              value={formData.stockQuantity}
              onChange={(e) => handleInputChange('stockQuantity', e.target.value)}
              placeholder="0"
              style={{
                ...inputStyle,
                borderColor: errors.stockQuantity ? '#dc3545' : '#ced4da'
              }}
            />
            {errors.stockQuantity && <div style={errorStyle}>{errors.stockQuantity}</div>}
          </div>
        </div>

        {/* Category and Brand */}
        <div style={rowStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Category *</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              placeholder="Enter category"
              list="categories"
              style={{
                ...inputStyle,
                borderColor: errors.category ? '#dc3545' : '#ced4da'
              }}
            />
            <datalist id="categories">
              {categories.map((category, index) => (
                <option key={index} value={category} />
              ))}
            </datalist>
            {errors.category && <div style={errorStyle}>{errors.category}</div>}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Brand</label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => handleInputChange('brand', e.target.value)}
              placeholder="Enter brand"
              list="brands"
              style={{
                ...inputStyle,
                borderColor: errors.brand ? '#dc3545' : '#ced4da'
              }}
            />
            <datalist id="brands">
              {brands.map((brand, index) => (
                <option key={index} value={brand} />
              ))}
            </datalist>
            {errors.brand && <div style={errorStyle}>{errors.brand}</div>}
          </div>
        </div>

        {/* Part Number */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Part Number *</label>
          <input
            type="text"
            value={formData.partNumber}
            onChange={(e) => handleInputChange('partNumber', e.target.value)}
            placeholder="Enter unique part number"
            style={{
              ...inputStyle,
              borderColor: errors.partNumber ? '#dc3545' : '#ced4da'
            }}
          />
          {errors.partNumber && <div style={errorStyle}>{errors.partNumber}</div>}
        </div>

        {/* Image URLs */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Product Images</label>
          {formData.imageUrls.map((url, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="url"
                value={url}
                onChange={(e) => handleImageUrlChange(index, e.target.value)}
                placeholder="Enter image URL"
                style={{ ...inputStyle, margin: 0 }}
              />
              {formData.imageUrls.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeImageUrl(index)}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '10px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addImageUrl}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            + Add Another Image
          </button>
        </div>

        {/* Submit Buttons */}
        <div style={buttonGroupStyle}>
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            style={cancelButtonStyle}
            disabled={isLoading}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            style={{
              ...submitButtonStyle,
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
            disabled={isLoading}
          >
            {isLoading 
              ? `${isEdit ? 'Updating' : 'Creating'}...` 
              : `${isEdit ? 'Update' : 'Create'} Product`
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;